import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Eye, EyeOff, Pencil, Mic } from "lucide-react";
import { API, COVER_PRESETS } from "@/lib/site";
import { CoverUpload } from "@/components/CoverUpload";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#060D17]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";
const labelCls = "font-mono-x mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-400";

const INITIAL = { title: "", guest_name: "", guest_role: "", description: "", youtube_url: "", cover_image: "", published: true };

export const AdminEpisodes = () => {
  const [episodes, setEpisodes] = useState([]);
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const headers = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("hs_admin_token")}` } });

  const load = useCallback(() => {
    axios
      .get(`${API}/episodes/all`, headers())
      .then(({ data }) => setEpisodes(data))
      .catch(() => toast.error("Could not load episodes."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/episodes/${editingId}`, form, headers());
        toast.success("Episode updated.");
      } else {
        await axios.post(`${API}/episodes`, form, headers());
        toast.success("Episode published.");
      }
      setForm(INITIAL);
      setEditingId(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save episode.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (ep) => {
    setEditingId(ep.id);
    setForm({
      title: ep.title,
      guest_name: ep.guest_name,
      guest_role: ep.guest_role || "",
      description: ep.description || "",
      youtube_url: ep.youtube_url || "",
      cover_image: ep.cover_image || "",
      published: ep.published,
    });
    document.querySelector('[data-testid="admin-episodes-section"]')?.scrollIntoView({ behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(INITIAL);
  };

  const togglePublish = async (ep) => {
    try {
      await axios.patch(
        `${API}/episodes/${ep.id}`,
        { title: ep.title, guest_name: ep.guest_name, guest_role: ep.guest_role, description: ep.description, youtube_url: ep.youtube_url, cover_image: ep.cover_image, published: !ep.published },
        headers()
      );
      load();
    } catch {
      toast.error("Could not update episode.");
    }
  };

  const remove = async (ep) => {
    if (!window.confirm(`Delete "${ep.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/episodes/${ep.id}`, headers());
      toast.success("Episode deleted.");
      load();
    } catch {
      toast.error("Could not delete episode.");
    }
  };

  return (
    <section data-testid="admin-episodes-section" className="mt-14">
      <div className="border-t border-white/5 pt-10">
        <h2 className="font-display flex items-center gap-3 text-xl font-bold text-white">
          <Mic className="h-5 w-5 text-teal-400" />
          Healthcare Connect Episodes
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Publish interview episodes — they appear instantly on the Healthcare Connect page.
        </p>
      </div>

      <form onSubmit={save} className="mt-6 rounded-2xl border border-white/5 bg-[#0B192C]/60 p-7">
        {editingId && (
          <p data-testid="episode-editing-banner" className="font-mono-x mb-5 text-[10px] uppercase tracking-[0.25em] text-amber-500">
            Editing existing episode — save to update or cancel below
          </p>
        )}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="e-title">Episode Title *</label>
            <input id="e-title" data-testid="episode-form-title" required minLength={4}
              placeholder="e.g. Building a Cashless-Ready Hospital" className={inputCls}
              value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls} htmlFor="e-guest">Guest Name *</label>
            <input id="e-guest" data-testid="episode-form-guest" required minLength={2}
              placeholder="e.g. Dr. A. Sharma" className={inputCls}
              value={form.guest_name} onChange={(e) => setForm((f) => ({ ...f, guest_name: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls} htmlFor="e-role">Guest Role / Title</label>
            <input id="e-role" data-testid="episode-form-role"
              placeholder="e.g. Director, City Hospital" className={inputCls}
              value={form.guest_role} onChange={(e) => setForm((f) => ({ ...f, guest_role: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls} htmlFor="e-youtube">YouTube URL (optional)</label>
            <input id="e-youtube" data-testid="episode-form-youtube" type="url"
              placeholder="https://www.youtube.com/watch?v=..." className={inputCls}
              value={form.youtube_url} onChange={(e) => setForm((f) => ({ ...f, youtube_url: e.target.value }))} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="e-desc">Description / Show Notes</label>
            <textarea id="e-desc" data-testid="episode-form-description" rows={4}
              placeholder="What this conversation covers..." className={`${inputCls} resize-y`}
              value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Cover Image (optional)</label>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {COVER_PRESETS.map((p) => (
                <button
                  type="button"
                  key={p.label}
                  data-testid={`episode-cover-preset-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setForm((f) => ({ ...f, cover_image: p.url }))}
                  title={p.label}
                  className={`overflow-hidden rounded-lg border-2 transition-all duration-300 ${
                    form.cover_image === p.url ? "border-teal-400" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={p.url} alt={p.label} loading="lazy" className="h-14 w-full object-cover" />
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-4">
              <CoverUpload onUploaded={(url) => setForm((f) => ({ ...f, cover_image: url }))} />
              {form.cover_image && (
                <button
                  type="button"
                  data-testid="episode-cover-clear"
                  onClick={() => setForm((f) => ({ ...f, cover_image: "" }))}
                  className="text-xs text-slate-500 underline transition-colors hover:text-red-400"
                >
                  Remove cover image
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="episode-form-submit"
            className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-[#060D17] transition-all duration-300 hover:bg-teal-400 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {saving ? "Saving..." : editingId ? "Save Changes" : "Publish Episode"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="episode-cancel-edit"
              className="rounded-full border border-white/10 px-6 py-3.5 text-xs font-semibold text-slate-300 transition-all duration-300 hover:border-red-500/40 hover:text-red-400"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3">
        {episodes.length === 0 && (
          <p className="rounded-2xl border border-white/5 bg-[#0B192C]/40 p-6 text-sm text-slate-500">
            No episodes yet — publish the first conversation above.
          </p>
        )}
        {episodes.map((ep) => (
          <div
            key={ep.id}
            data-testid={`admin-episode-row-${ep.slug}`}
            className="flex flex-col gap-4 rounded-2xl border border-white/5 bg-[#0B192C]/40 p-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="font-display truncate text-sm font-bold text-white">{ep.title}</p>
              <p className="font-mono-x mt-1 text-[10px] uppercase tracking-[0.2em] text-teal-400">
                {ep.guest_name} ·{" "}
                <span className={ep.published ? "text-emerald-400" : "text-slate-500"}>
                  {ep.published ? "Published" : "Hidden"}
                </span>
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => startEdit(ep)}
                data-testid={`episode-edit-${ep.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-all hover:border-amber-500/40 hover:text-amber-400"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
              <button
                onClick={() => togglePublish(ep)}
                data-testid={`episode-toggle-${ep.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-all hover:border-teal-500/40 hover:text-teal-300"
              >
                {ep.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {ep.published ? "Hide" : "Publish"}
              </button>
              <button
                onClick={() => remove(ep)}
                data-testid={`episode-delete-${ep.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-all hover:border-red-500/40 hover:text-red-400"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
